// filepath: apps/web/src/admin/counterparties/detail.tsx
//
// FR-Group-B — CounterpartyDetailScreen (ISS-442 UX slim + i18n + date).
//
// Read-only detail view. Shows the full counterparty record + the
// audit-trail sub-list (newest first). Every PII field that is
// server-stripped (INV-2) renders an em-dash placeholder so the
// layout doesn't shift when an admin views the same row.
//
// ISS-442 BA spec (verbatim):
//   (a) Move legal_form and tax_id from the list to the detail and
//       edit views.
//   (b) Translation all locales (RU authoritative, KK falls back
//       to RU when a key is missing, EN must have real English).
//       No hardcoded strings. i18n namespace
//       `directory.counterparties.*`.
//   (c) Date format `dd-MM-yyyy HH:mm` in the list + detail view,
//       through ONE shared formatter in the user's browser
//       timezone. Lowercase `yyyy` only.
//
// ISS-431-uatrzt2 React crash fix: the project explicitly opts out
// of `react-router-dom` (FR-UI-CABINET-1 AC-9) and uses its own
// `usePathname()` / `navigate()` primitives. The previous
// `Link` + `useParams` imports from `react-router-dom` threw on
// every render (no `<Router>` in the production app).
//
// INV-9: no `dangerouslySetInnerHTML`; the audit-trail row's
// `metadata` JSON is NEVER rendered — only `kind`, `author_identity`,
// `version_before`, `version_after`, `timestamp`. The metadata body
// stays in JS state for the audit-detail modal (out of scope for
// this screen) but is never interpolated into the DOM as text.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { DocumentHeader } from "@/design-system/DocumentHeader";
import { navigate } from "@/features/cabinet/usePathname";
import {
  Counterparty,
  CounterpartyAuditEntry,
  getAuditTrailForCounterparty,
  getCounterparty,
} from "./api";

/**
 * ISS-431-uatrzt2 React crash fix: see list.tsx for the full
 * rationale. Plain `<a href>` + onClick → `navigate(href)` (left-
 * click without modifier); modifier-clicks + non-primary buttons
 * fall through to the browser default for "open in new tab" etc.
 */
function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

export interface CounterpartyDetailScreenProps {
  /**
   * ISS-379 (Slice 0 tenant-alive): the route id from App.tsx's
   * path-matching dispatch (/admin/counterparties/:id -> id
   * segment). Required in production (App.tsx always passes it);
   * empty string triggers the "not found" render path.
   */
  id?: string;
  /** Test override - defaults to fetching from canonical REST. */
  fetcher?: (id: string) => Promise<Counterparty>;
  /** Test override for audit-trail fetch. */
  auditFetcher?: (id: string) => Promise<CounterpartyAuditEntry[]>;
}

export function CounterpartyDetailScreen({
  id = "",
  fetcher,
  auditFetcher,
}: CounterpartyDetailScreenProps = {}): React.ReactElement {
  const { t, locale } = useTranslation();

  const [row, setRow] = useState<Counterparty | null>(null);
  const [audit, setAudit] = useState<CounterpartyAuditEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const f = fetcher ?? ((cid: string) => getCounterparty(cid));
      const a =
        auditFetcher ??
        ((cid: string) => getAuditTrailForCounterparty(cid, { limit: 50 }));
      const [cp, ae] = await Promise.all([f(id), a(id)]);
      setRow(cp);
      setAudit(ae);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id, fetcher, auditFetcher]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <p data-testid="counterparty-detail-loading">
        {t("directory.counterparties.detail.loading", "Loading counterparty…")}
      </p>
    );
  }
  if (error) {
    return (
      <p role="alert" data-testid="counterparty-detail-error">
        {t(
          "directory.counterparties.detail.error",
          "Failed to load counterparty: {{message}}",
          { message: error },
        )}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="counterparty-detail-not-found">
        {t(
          "directory.counterparties.detail.not_found",
          "Counterparty not found",
        )}
      </p>
    );
  }

  return (
    <section data-testid="counterparty-detail-screen">
      {/* ISS-513 (Slice 1 conformance sweep, operator visual
          inspection 2026-10-20): the page-level header now uses
          the design-system <DocumentHeader /> primitive. The
          previous bare <header><h1/></header> rendered without
          the design-system's surface / spacing / typography
          conventions (the operator's eyes-on defect). The
          DocumentHeader surfaces the title + status badge +
          right-aligned actions in the canonical layout used by
          the rest of the directory surfaces. The actions slot
          keeps the same Edit / Back anchor-wrapped buttons
          (the SPA opt-out of react-router-dom; see new.tsx for
          the same pattern). */}
      <DocumentHeader
        title={row.name}
        status={t(`directory.counterparties.status.${row.status}`, row.status)}
        statusTone={row.status === "active" ? "success" : "neutral"}
        data-testid="counterparty-detail-header"
        actions={
          <>
            <a
              href={`/admin/counterparties/${encodeURIComponent(row.id)}/edit`}
              data-testid="counterparty-detail-edit-link"
              onClick={(e) =>
                handleSpaNavigate(
                  e,
                  `/admin/counterparties/${encodeURIComponent(row.id)}/edit`,
                )
              }
              style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (button-styled anchor; no underline)
            >
              <Button variant="primary">
                {t("directory.counterparties.detail.edit", "Edit")}
              </Button>
            </a>
            <a
              href="/admin/counterparties"
              data-testid="counterparty-detail-back-link"
              onClick={(e) => handleSpaNavigate(e, "/admin/counterparties")}
              style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (button-styled anchor; no underline)
            >
              <Button variant="secondary">
                {t("directory.counterparties.detail.back", "Back")}
              </Button>
            </a>
          </>
        }
      />

      <Card>
        <dl className="counterparty-detail-fields">
          <dt>{t("directory.counterparties.detail.field.name", "Name")}</dt>
          <dd>{row.name}</dd>
          {/* ISS-442 (a): legal_form + tax_id moved from the list to
              the detail + edit views. The list now shows only
              name / status / last update (per BA spec). */}
          <dt>
            {t(
              "directory.counterparties.detail.field.legal_form",
              "Legal form",
            )}
          </dt>
          <dd>
            {t(
              `directory.counterparties.legal_form.${row.legal_form}`,
              row.legal_form,
            )}
          </dd>
          <dt>{t("directory.counterparties.detail.field.tax_id", "Tax id")}</dt>
          <dd>{row.tax_id ?? "—"}</dd>
          {/* ISS-448 BA spec: contact_person_id is OUT of v1
              (needs a person picker; BA deferred to a separate
              FR). The detail screen no longer renders the field. */}
          <dt>{t("directory.counterparties.detail.field.email", "Email")}</dt>
          <dd>{row.email ?? "—"}</dd>
          <dt>{t("directory.counterparties.detail.field.phone", "Phone")}</dt>
          <dd>{row.phone ?? "—"}</dd>
          <dt>
            {t("directory.counterparties.detail.field.address", "Address")}
          </dt>
          <dd>{row.address ?? "—"}</dd>
          <dt>{t("directory.counterparties.detail.field.notes", "Notes")}</dt>
          <dd>{row.notes ?? "—"}</dd>
          <dt>{t("directory.counterparties.detail.field.status", "Status")}</dt>
          <dd>
            <Badge tone={row.status === "active" ? "success" : "neutral"}>
              {t(`directory.counterparties.status.${row.status}`, row.status)}
            </Badge>
          </dd>
          <dt>
            {t(
              "directory.counterparties.detail.field.row_version",
              "Row version (OCC)",
            )}
          </dt>
          <dd>{row.row_version}</dd>
          {/* ISS-442 (c): date format `dd-MM-yyyy HH:mm` in the
              browser's IANA timezone. Lowercase `yyyy` for calendar
              year (NOT YYYY which is week-year). */}
          <dt>
            {t(
              "directory.counterparties.detail.field.created_at",
              "Created at",
            )}
          </dt>
          <dd>{formatDatePattern(new Date(row.created_at), locale)}</dd>
          <dt>
            {t(
              "directory.counterparties.detail.field.created_by_subject",
              "Created by subject",
            )}
          </dt>
          <dd>{row.created_by_subject}</dd>
          <dt>
            {t(
              "directory.counterparties.detail.field.updated_at",
              "Last update",
            )}
          </dt>
          <dd>{formatDatePattern(new Date(row.updated_at), locale)}</dd>
          <dt>
            {t(
              "directory.counterparties.detail.field.updated_by_subject",
              "Updated by subject",
            )}
          </dt>
          <dd>{row.updated_by_subject}</dd>
        </dl>
      </Card>

      {/* ISS-513 (Slice 1 conformance sweep): the audit-trail
          section is now wrapped in <Card> so the table sits on
          the same surface primitive the rest of the directory
          uses. The pre-fix bare <section><table/></section>
          rendered the table on the bare body surface (no card
          border, no padding, no radius) — the operator's
          eyes-on defect. The data-testid on the section +
          table is preserved so the existing routing regression
          continues to find the audit nodes. */}
      <Card>
        <section data-testid="counterparty-detail-audit">
          <h2 id="counterparty-detail-audit-title">
            {t("directory.counterparties.detail.audit_title", "Audit trail")}
          </h2>
          {audit.length === 0 ? (
            <p data-testid="counterparty-detail-audit-empty">
              {t(
                "directory.counterparties.detail.audit_empty",
                "No audit entries yet",
              )}
            </p>
          ) : (
            <table aria-labelledby="counterparty-detail-audit-title">
              <thead>
                <tr>
                  <th scope="col">Timestamp</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Author</th>
                  <th scope="col">Version before</th>
                  <th scope="col">Version after</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} data-testid={`counterparty-audit-row-${e.id}`}>
                    <td>{e.timestamp}</td>
                    <td>{e.kind}</td>
                    <td>{e.author_identity}</td>
                    <td>{e.version_before ?? "—"}</td>
                    <td>{e.version_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </Card>
    </section>
  );
}

export default CounterpartyDetailScreen;
